import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import Sidebar from "../Sidebar/Sidebar";
import config from "../../config";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const drawerWidth = 270;

const getToday = () => dayjs().format("YYYY-MM-DD");

const sectionCardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(15,23,42,0.06)",
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
};

const tableHeadSx = {
  fontWeight: 800,
  fontSize: "0.76rem",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#475569",
  background: "#F8FAFC",
  borderBottom: "2px solid rgba(15,23,42,0.08)",
  borderRight: "1px solid rgba(15,23,42,0.10)",
  whiteSpace: "nowrap",
};

const tableCellSx = {
  fontSize: "0.85rem",
  borderBottom: "1px solid rgba(15,23,42,0.07)",
  borderRight: "1px solid rgba(15,23,42,0.08)",
};

const PendingMandiReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [date, setDate] = useState(getToday());
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const abortRef = useRef(null);
  const token = sessionStorage.getItem("token");

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      params.set("page", String(page + 1));
      params.set("limit", String(rowsPerPage));

      const res = await fetch(`${config.apiBaseUrl}/campaign-call/partner-pending-report?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const json = await res.json();
      if (json?.success === false) throw new Error(json?.message || "Failed to load pending mandi reports.");

      setRows(Array.isArray(json?.data) ? json.data : []);
      setPagination(json?.pagination ?? null);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Failed to load pending mandi reports.");
    } finally {
      setLoading(false);
    }
  }, [token, date, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [date]);

  const handleDateChange = (newValue) => {
    if (!newValue) return;
    const value = newValue.format("YYYY-MM-DD");
    if (value > getToday()) return;
    setDate(value);
  };

  const handleToday = () => setDate(getToday());

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  const filteredRows = !search.trim()
    ? rows
    : rows.filter((r) => {
        const term = search.trim().toLowerCase();
        return (
          String(r.mandiPartner || "").toLowerCase().includes(term) ||
          String(r.mandiLocation || "").toLowerCase().includes(term) ||
          String(r.businessName || "").toLowerCase().includes(term) ||
          String(r.state || "").toLowerCase().includes(term) ||
          String(r.mandiCode || "").toLowerCase().includes(term)
        );
      });

  const totalEntries = pagination?.total ?? 0;
  const pageCount = pagination?.totalPages || 1;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 1.5, sm: 2.5, md: 4 },
          overflowX: "hidden",
        }}
      >
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1.5} sx={{ mb: { xs: 2, md: 3 } }}>
          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ border: "1px solid #E0E0E0", borderRadius: "12px" }}>
              <MenuRoundedIcon />
            </IconButton>
          )}

          <IconButton
            onClick={() => navigate(-1)}
            sx={{ border: "1px solid rgba(15,23,42,0.12)", borderRadius: "12px" }}
            aria-label="Go back"
          >
            <ArrowBackRoundedIcon />
          </IconButton>

          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: "linear-gradient(135deg,#DC2626 0%,#F97316 100%)",
            }}
          >
            <EventBusyRoundedIcon sx={{ fontSize: 27 }} />
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800}>
              Pending Mandi Reports
            </Typography>
            <Typography sx={{ color: "rgba(15,23,42,0.55)", fontSize: "0.85rem" }}>
              {totalEntries ? `${totalEntries} pending partner${totalEntries === 1 ? "" : "s"}` : "Mandi partners yet to submit their report"}
              {date ? ` · ${dayjs(date).format("DD MMM YYYY")}` : ""}
            </Typography>
          </Box>
        </Stack>

        <Card elevation={0} sx={{ ...sectionCardSx, p: { xs: 1.25, sm: 1.75 }, mb: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                }}
              >
                <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>Select date</Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={date ? dayjs(date) : null}
                  onChange={handleDateChange}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: "small",
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthRoundedIcon sx={{ fontSize: 17, color: "rgba(15,23,42,0.45)" }} />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                  sx={{ width: { xs: "100%", sm: 170 }, "& .MuiOutlinedInput-root": { borderRadius: "9px" } }}
                />
              </LocalizationProvider>

              <Chip
                onClick={handleToday}
                icon={<TodayRoundedIcon sx={{ fontSize: 16 }} />}
                label="Today"
                clickable
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  borderRadius: "9px",
                  background: date === getToday() ? "rgba(37,99,235,0.10)" : "transparent",
                  color: date === getToday() ? "#1D4ED8" : "rgba(15,23,42,0.6)",
                  border: "1px solid rgba(15,23,42,0.12)",
                }}
              />
            </Stack>
          </Stack>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, md: 3 }, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        <Card elevation={0} sx={{ ...sectionCardSx, p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.25}
            mb={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <StorefrontRoundedIcon sx={{ color: "#DC2626" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Pending Partners</Typography>
              {!loading && (
                <Chip
                  size="small"
                  label={totalEntries}
                  sx={{ fontWeight: 800, background: "rgba(220,38,38,0.10)", color: "#B91C1C" }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <TextField
                size="small"
                placeholder="Search partner, mandi, business..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!rows.length}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: 0, sm: 240 }, flexGrow: { xs: 1, sm: 0 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <Select
                size="small"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{ borderRadius: "10px", minWidth: 90 }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>{n} / page</MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>

          {loading ? (
            <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
              <Table
                sx={{
                  "& th:last-of-type, & td:last-of-type": { borderRight: "none" },
                  "& tr:last-of-type td": { borderBottom: "none" },
                }}
              >
                <TableHead>
                  <TableRow sx={{ background: "rgba(15,23,42,0.02)" }}>
                    {["#", "Mandi Partner", "Mandi", "Business", "State", "Contact", "Status"].map((h) => (
                      <TableCell key={h} sx={tableHeadSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 8 }, (_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }, (_, c) => (
                        <TableCell key={c} sx={tableCellSx}>
                          <Skeleton variant="text" width={c === 0 ? 20 : "80%"} height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : rows.length === 0 ? (
            <Box
              sx={{
                minHeight: 220,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(22,163,74,0.10)",
                  color: "#16A34A",
                  mb: 1.5,
                }}
              >
                <StorefrontRoundedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>No pending reports</Typography>
              <Typography sx={{ color: "rgba(15,23,42,0.5)", fontSize: "0.85rem", mt: 0.5 }}>
                All mandi partners have submitted their report for this date.
              </Typography>
            </Box>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
              <Table
                sx={{
                  "& th, & td": {
                    borderBottom: "1px solid rgba(15,23,42,0.10)",
                    borderRight: "1px solid rgba(15,23,42,0.10)",
                  },
                  "& th:last-of-type, & td:last-of-type": { borderRight: "none" },
                  "& tr:last-of-type td": { borderBottom: "none" },
                }}
              >
                <TableHead>
                  <TableRow sx={{ background: "rgba(15,23,42,0.02)" }}>
                    <TableCell sx={tableHeadSx}>#</TableCell>
                    <TableCell sx={tableHeadSx}>Mandi Partner</TableCell>
                    <TableCell sx={tableHeadSx}>Mandi</TableCell>
                    {/* <TableCell sx={tableHeadSx}>Business</TableCell> */}
                    {/* <TableCell sx={tableHeadSx}>State</TableCell> */}
                    <TableCell sx={tableHeadSx}>Contact</TableCell>
                    <TableCell sx={tableHeadSx}>Status</TableCell>
                    <TableCell sx={tableHeadSx}>Reminder </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length > 0 ? (
                    filteredRows.map((row, idx) => (
                      <TableRow key={row._id || idx} hover>
                        <TableCell sx={{ ...tableCellSx, color: "rgba(15,23,42,0.55)" }}>
                          {page * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell sx={tableCellSx}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <BadgeRoundedIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
                            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                              {row.mandiPartner || "—"}
                            </Typography>
                          </Stack>
                          {row.mandiCode && (
                            <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.45)", ml: 2.75 }}>
                              Code: {row.mandiCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={tableCellSx}>
                          <Chip
                            icon={<StorefrontRoundedIcon sx={{ fontSize: 15 }} />}
                            label={row.mandiLocation || "—"}
                            size="small"
                            sx={{ background: "rgba(22,163,74,0.10)", color: "#166534", fontWeight: 600, fontSize: "0.78rem" }}
                          />
                        </TableCell>
                        {/* <TableCell sx={{ ...tableCellSx, maxWidth: 220 }}>
                          <Typography noWrap sx={{ fontSize: "0.82rem" }}>
                            {row.businessName || "—"}
                          </Typography>
                        </TableCell> */}
                        {/* <TableCell sx={tableCellSx}>
                          <Typography sx={{ fontSize: "0.82rem", color: "rgba(15,23,42,0.7)" }}>
                            {row.state || "—"}
                          </Typography>
                        </TableCell> */}
                        <TableCell sx={tableCellSx}>
                          {row.contactNumber ? (
                            <Stack direction="row" spacing={0.6} alignItems="center">
                              <CallRoundedIcon sx={{ fontSize: 15, color: "rgba(15,23,42,0.45)" }} />
                              <Typography
                                component="a"
                                href={`tel:${row.contactNumber}`}
                                sx={{ fontSize: "0.82rem", color: "#2563EB", textDecoration: "none", fontWeight: 600 }}
                              >
                                {row.contactNumber}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography sx={{ color: "rgba(15,23,42,0.35)" }}>—</Typography>
                          )}
                          {row.alternateNumber && (
                            <Typography sx={{ fontSize: "0.7rem", color: "rgba(15,23,42,0.45)", ml: 2.6 }}>
                              Alt: {row.alternateNumber}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={tableCellSx}>
                          <Chip
                            size="small"
                            label={row.reportStatus || "pending"}
                            sx={{
                              textTransform: "capitalize",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              background: "rgba(220,38,38,0.10)",
                              color: "#B91C1C",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...tableCellSx, maxWidth: 220 }}>
                          <Typography noWrap sx={{ fontSize: "0.82rem" }}>
                            {row.retryCount || "0"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", py: 5 }}>
                        <Typography sx={{ color: "rgba(15,23,42,0.5)" }}>No matching rows on this page</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}

          {rows.length > 0 && pagination && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1.5} mt={2}>
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.55)" }}>
                Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalEntries)} of {totalEntries} entries
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  aria-label="Previous page"
                  disabled={!pagination.hasPreviousPage || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px" }}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {Array.from({ length: pageCount }, (_, i) => i).map((p) => (
                  <Box
                    key={p}
                    onClick={() => !loading && setPage(p)}
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      cursor: loading ? "default" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      border: p === page ? "1px solid #2563EB" : "1px solid transparent",
                      color: p === page ? "#2563EB" : "#0F172A",
                      background: p === page ? "rgba(37,99,235,0.08)" : "transparent",
                    }}
                  >
                    {p + 1}
                  </Box>
                ))}

                <IconButton
                  size="small"
                  aria-label="Next page"
                  disabled={!pagination.hasNextPage || loading}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px" }}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default PendingMandiReport;